import React, { useState, useEffect, useRef } from "react";
import { IoCloseSharp } from "react-icons/io5";
import axios from "../../config/axios";
import live_display_socket from "../../config/live_display_socket";
import { updateActivationCodeList } from "../../config/updateConfig";
import { useTV } from "../../hooks/useTV";
import useGlobalStore from "../../store/global.store";
import { buttonIds } from "../../config/buttonIds";
import LivePianoSettings from "./LivePianoSettings";
const Settings = ({ device }) => {
  const setShowSettings = useGlobalStore((state) => state.setShowSettings);
  const settingsInitialView = useGlobalStore(
    (state) => state.settingsInitialView
  );
  const setSettingsInitialView = useGlobalStore(
    (state) => state.setSettingsInitialView
  );

  const urlParams = new URLSearchParams(window.location.search);
  const [view, setView] = useState(settingsInitialView);
  const [focusedButton, setFocusedButton] = useState(null);
  const [volume, setVolume] = useState(
    localStorage.getItem("volume")
      ? parseFloat(localStorage.getItem("volume")) * 10
      : 10
  );
  const [showPassInput, setShowPassInput] = useState(false);
  const [passcode, setPasscode] = useState("");
  const [vacationDate, setVacationDate] = useState("");
  const [vacationDateError, setVacationDateError] = useState("");
  const { isTv } = useTV();
  const wifiButtonRef = useRef(null);
  const firstAutoScrollButtonRef = useRef(null);
  const firstTimezoneButtonRef = useRef(null);
  const firstTRCButtonRef = useRef(null);
  const firstVacationButtonRef = useRef(null);
  const firstLivePianoButtonRef = useRef(null);

  // Auto-focus WiFi Settings button when component mounts
  useEffect(() => {
    if (view === "all" && wifiButtonRef.current && isTv) {
      setFocusedButton("wifi");
      wifiButtonRef.current.focus();
    } else if (
      view === "AutoScroll" &&
      firstAutoScrollButtonRef.current &&
      isTv
    ) {
      setFocusedButton("autoscroll-5");
      firstAutoScrollButtonRef.current.focus();
    } else if (view === "TimeZone" && firstTimezoneButtonRef.current && isTv) {
      setFocusedButton("timezone-est");
      firstTimezoneButtonRef.current.focus();
    } else if (view === "TRC" && firstTRCButtonRef.current && isTv) {
      setFocusedButton("announcement-toggle");
      firstTRCButtonRef.current.focus();
    } else if (
      view === "VacationDate" &&
      firstVacationButtonRef.current &&
      isTv
    ) {
      setFocusedButton("vacation-date-input");
      firstVacationButtonRef.current.focus();
    } else if (
      view === "LivePiano" &&
      firstLivePianoButtonRef.current &&
      isTv
    ) {
      setFocusedButton("live-piano-autostart-toggle");
      firstLivePianoButtonRef.current.focus();
    }
  }, [view, isTv]);

  // Reset settingsInitialView to "all" when component unmounts
  useEffect(() => {
    return () => {
      setSettingsInitialView("all");
    };
  }, [setSettingsInitialView]);

  // Update view when settingsInitialView changes
  useEffect(() => {
    if (settingsInitialView !== "all") {
      setView(settingsInitialView);
    }
  }, [settingsInitialView]);

  const updateDeviceView = (view) => {
    axios
      .patch(
        `/users/device/${device._id}`,
        { screen_view: view },
        { withCredentials: true }
      )
      .then(() => {
        live_display_socket.emit("remote_refresh", {
          device_id: device?._id,
        });
      })
      .catch((error) => {});
  };

  const goToWifiSettingsPageOnExpoApp = () => {
    window.ReactNativeWebView.postMessage(
      JSON.stringify({
        action: "open-wifi-settings",
      })
    );
  };

  const handleDeviceScrollIntervalUpdate = (value) => {
    axios
      .patch(
        `/users/device/${device._id}`,
        { auto_scroll_interval: value },
        { withCredentials: true }
      )
      .then(() => {
        live_display_socket.emit("remote_refresh", {
          device_id: device?._id,
        });
      })
      .catch((error) => {});
  };

  const updateTimezone = (timezone) => {
    axios
      .patch(
        `/users/device/${device._id}`,
        { timezone: timezone },
        { withCredentials: true }
      )
      .then(() => {
        setShowSettings(false);
        live_display_socket.emit("remote_refresh", {
          device_id: device?._id,
        });
      })
      .catch((error) => {
        // console.log(error)
      });
  };

  const updateDevice = (payload) => {
    axios
      .patch(`/users/device/${device._id}`, payload, { withCredentials: true })
      .then(() => {
        live_display_socket.emit("remote_refresh", {
          device_id: device?._id,
        });
      })
      .catch((error) => {
        // console.log(error)
      });
  };

  const handleVacationDateSubmit = () => {
    if (!vacationDate) {
      setVacationDateError("Please select your vacation date");
      return;
    }

    // Clear any existing error
    setVacationDateError("");

    const payload = {
      device_id: device._id,
      cf_vacation_countdown_date: vacationDate,
    };

    axios
      .post("/users/update-cf-vacation-countdown", payload, {
        withCredentials: true,
      })
      .then(() => {
        setShowSettings(false);
        live_display_socket.emit("remote_refresh", {
          device_id: device?._id,
        });
        // Reset form
        setVacationDate("");
        setVacationDateError("");
      })
      .catch((error) => {
        console.log("Error updating vacation date:", error);
        setVacationDateError(
          "Failed to update vacation date. Please try again."
        );
      });
  };

  // Button style with focus handling
  const getButtonStyle = (buttonId, baseStyle) => {
    const focusedStyle =
      isTv && focusedButton === buttonId
        ? {
            border: "6px solid #3ADF3C",
            outline: "none",
          }
        : {};

    return {
      ...baseStyle,
      ...focusedStyle,
    };
  };

  // Base button style
  const baseButtonStyle = {
    marginLeft: "10px",
    border: isTv ? "6px solid blue" : "2px solid blue",
    background: "transparent",
    color: "blue",
    fontFamily: "Lato",
    fontSize: isTv ? "60px" : "35px",
    padding: isTv ? "10px 25px" : "0px 20px",
    borderRadius: "20px",
    fontWeight: "700",
    margin: "10px 0px",
    cursor: "pointer",
  };

  // Exit button style
  const exitButtonStyle = {
    ...baseButtonStyle,
    border: isTv ? "6px solid red" : "2px solid red",
    color: "red",
    marginTop: "20px",
  };

  const isUpdateAvailable = () => {
    if (isTv) {
      console.log(urlParams.get("v"));
      if (urlParams.get("v") === "6") {
        return true;
      } else {
        return false;
      }
    } else {
      return (
        urlParams.get("v") !== "13" &&
        updateActivationCodeList.includes(device.subscription._id)
      );
    }
  };

  const handleUpdate = () => {
    if (isTv) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          action: "open-download-update",
          uri: "https://codenine.b-cdn.net/build-1753259548977.apk",
        })
      );
    } else {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          action: "open-download-update",
          uri: "https://wait-signs-static.s3.us-east-1.amazonaws.com/build-1735401314723.apk",
        })
      );
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        position: "absolute",
        top: "0%",
        backgroundColor: "white",
        width: "100vw",
        height: "100vh",
        zIndex: 250,
        opacity: "90%",
      }}
    >
      {!isTv && (
        <button
          tabIndex={0}
          onFocus={() => setFocusedButton("close")}
          onBlur={() => setFocusedButton(null)}
          onClick={() => {
            setShowSettings(false);
            document.getElementById(buttonIds.settings).focus();
          }}
          style={{
            position: "absolute",
            top: "5%",
            right: "3%",
            background: "transparent",
            border:
              isTv && focusedButton === "close"
                ? "6px solid #3ADF3C"
                : "6px solid transparent",
            borderRadius: "50%",
            padding: "5px",
            cursor: "pointer",
            outline: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <IoCloseSharp size={isTv ? 100 : 60} />
        </button>
      )}

      {view === "AutoScroll" ? (
        <>
          <div
            style={{
              display: "flex",
              width: "80vw",
              flexWrap: "wrap",
              gap: "10px",
            }}
          >
            {[...Array(12)].map((_, i) => {
              const interval = (i + 1) * 5;
              const buttonId = `autoscroll-${interval}`;
              return (
                <button
                  key={buttonId}
                  ref={i === 0 ? firstAutoScrollButtonRef : null}
                  tabIndex={0}
                  onFocus={() => setFocusedButton(buttonId)}
                  onBlur={() => setFocusedButton(null)}
                  onClick={() => {
                    setShowSettings(false);
                    handleDeviceScrollIntervalUpdate(interval);
                    updateDeviceView("AutoScroll");
                  }}
                  style={getButtonStyle(buttonId, baseButtonStyle)}
                >
                  {interval} seconds
                </button>
              );
            })}

            {isTv && (
              <button
                tabIndex={0}
                onFocus={() => setFocusedButton("exit")}
                onBlur={() => setFocusedButton(null)}
                onClick={() => {
                  setView("all");
                }}
                style={getButtonStyle("exit", exitButtonStyle)}
              >
                Close
              </button>
            )}
          </div>
        </>
      ) : view === "TimeZone" ? (
        <>
          <div style={{ display: "flex", gap: "10px" }}>
            <button
              ref={firstTimezoneButtonRef}
              tabIndex={0}
              onFocus={() => setFocusedButton("timezone-est")}
              onBlur={() => setFocusedButton(null)}
              onClick={() => {
                updateTimezone("EST-Eastern Daylight Time,(US/Canada)");
              }}
              style={getButtonStyle("timezone-est", baseButtonStyle)}
            >
              <span>Orlando, Florida</span>
            </button>
            <button
              tabIndex={0}
              onFocus={() => setFocusedButton("timezone-pst")}
              onBlur={() => setFocusedButton(null)}
              onClick={() => {
                updateTimezone("PST-Pacific Standard Time,(US/Canada)");
              }}
              style={getButtonStyle("timezone-pst", baseButtonStyle)}
            >
              <span>Anaheim, California</span>
            </button>
            <button
              tabIndex={0}
              onFocus={() => setFocusedButton("timezone-my")}
              onBlur={() => setFocusedButton(null)}
              onClick={() => {
                updateTimezone("my timezone");
              }}
              style={getButtonStyle("timezone-my", baseButtonStyle)}
            >
              <span>My Time Zone</span>
            </button>
          </div>
          {isTv && (
            <button
              tabIndex={0}
              onFocus={() => setFocusedButton("exit")}
              onBlur={() => setFocusedButton(null)}
              onClick={() => {
                setView("all");
              }}
              style={getButtonStyle("exit", exitButtonStyle)}
            >
              Close
            </button>
          )}
        </>
      ) : view === "Volume" ? (
        <>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <input
              className={"range"}
              type="range"
              min="0"
              max="10"
              style={{ width: "500px" }}
              onChange={(e) => {
                let value = e.target.value;
                setVolume(value);
                value = value / 10;
                localStorage.setItem("volume", value);
              }}
              value={volume}
            />
            <button
              style={{
                marginLeft: "10px",
                background: "transparent",
                border: "none",
                color: "blue",
                fontFamily: "Lato",
                fontSize: "70px",
                padding: "0px 20px",
                borderRadius: "20px",
                fontWeight: "700",
                margin: "10px 0px",
                cursor: "pointer",
                marginTop: "40px",
              }}
            >
              {volume}
            </button>
            <div
              style={{
                display: "flex",
                gap: "10px",
              }}
            >
              <button
                tabIndex={0}
                onFocus={() => setFocusedButton("volume-mute")}
                onBlur={() => setFocusedButton(null)}
                style={getButtonStyle("volume-mute", {
                  marginLeft: "10px",
                  background: "transparent",
                  border: "2px solid blue",
                  color: "blue",
                  fontFamily: "Lato",
                  fontSize: "50px",
                  padding: "0px 20px",
                  borderRadius: "20px",
                  fontWeight: "700",
                  margin: "10px 0px",
                  cursor: "pointer",
                  marginTop: "40px",
                })}
                onClick={() => {
                  if (volume === 0) {
                    setVolume(3);
                    localStorage.setItem("volume", 0.3);
                  } else {
                    setVolume(0);
                    localStorage.setItem("volume", 0);
                  }
                }}
              >
                {parseInt(volume) === 0 ? "Unmute" : "Mute"}
              </button>
              <button
                tabIndex={0}
                onFocus={() => setFocusedButton("volume-down")}
                onBlur={() => setFocusedButton(null)}
                style={getButtonStyle("volume-down", {
                  marginLeft: "10px",
                  background: "transparent",
                  border: "2px solid blue",
                  color: "blue",
                  fontFamily: "Lato",
                  fontSize: "50px",
                  padding: "0px 20px",
                  borderRadius: "20px",
                  fontWeight: "700",
                  margin: "10px 0px",
                  cursor: "pointer",
                  marginTop: "40px",
                })}
                onClick={() => {
                  if (volume !== 0) {
                    setVolume(volume - 1);
                    localStorage.setItem("volume", (volume - 1) / 10);
                  }
                }}
              >
                -
              </button>
              <button
                tabIndex={0}
                onFocus={() => setFocusedButton("volume-up")}
                onBlur={() => setFocusedButton(null)}
                style={getButtonStyle("volume-up", {
                  marginLeft: "10px",
                  background: "transparent",
                  border: "2px solid blue",
                  color: "blue",
                  fontFamily: "Lato",
                  fontSize: "50px",
                  padding: "0px 20px",
                  borderRadius: "20px",
                  fontWeight: "700",
                  margin: "10px 0px",
                  cursor: "pointer",
                  marginTop: "40px",
                })}
                onClick={() => {
                  if (volume !== 10) {
                    setVolume(volume + 1);
                    localStorage.setItem("volume", (volume + 1) / 10);
                  }
                }}
              >
                +
              </button>
            </div>
          </div>
        </>
      ) : view === "TRC" ? (
        <div>
          {isTv ? (
            // TV interface with buttons
            <>
              <div
                style={{ display: "flex", gap: "20px", marginBottom: "20px" }}
              >
                <span
                  style={{
                    fontSize: "85px",
                    color: "blue",
                    fontFamily: "Lato",
                  }}
                >
                  Announcement:
                </span>
                <button
                  ref={firstTRCButtonRef}
                  tabIndex={0}
                  onFocus={() => setFocusedButton("announcement-toggle")}
                  onBlur={() => setFocusedButton(null)}
                  onClick={() => {
                    updateDevice({ announcement: !device?.announcement });
                  }}
                  style={getButtonStyle("announcement-toggle", {
                    background: device?.announcement ? "blue" : "transparent",
                    border: "6px solid blue",
                    color: device?.announcement ? "white" : "blue",
                    fontFamily: "Lato",
                    fontSize: "60px",
                    padding: "10px 25px",
                    borderRadius: "20px",
                    fontWeight: "700",
                    cursor: "pointer",
                  })}
                >
                  {device?.announcement ? "ON" : "OFF"}
                </button>
              </div>

              <div
                style={{ display: "flex", gap: "20px", marginBottom: "40px" }}
              >
                <span
                  style={{
                    fontSize: "85px",
                    color: "blue",
                    fontFamily: "Lato",
                  }}
                >
                  Music:
                </span>
                <button
                  tabIndex={0}
                  onFocus={() => setFocusedButton("music-toggle")}
                  onBlur={() => setFocusedButton(null)}
                  onClick={() => {
                    updateDevice({ trc_music: !device?.trc_music });
                  }}
                  style={getButtonStyle("music-toggle", {
                    background: device?.trc_music ? "blue" : "transparent",
                    border: "6px solid blue",
                    color: device?.trc_music ? "white" : "blue",
                    fontFamily: "Lato",
                    fontSize: "60px",
                    padding: "10px 25px",
                    borderRadius: "20px",
                    fontWeight: "700",
                    cursor: "pointer",
                  })}
                >
                  {device?.trc_music ? "ON" : "OFF"}
                </button>
              </div>

              <button
                tabIndex={0}
                onFocus={() => setFocusedButton("trc-close")}
                onBlur={() => setFocusedButton(null)}
                onClick={() => {
                  setView("all");
                }}
                style={getButtonStyle("trc-close", exitButtonStyle)}
              >
                Close
              </button>
            </>
          ) : (
            // Original touch interface with checkboxes
            <>
              <div>
                <span
                  style={{
                    fontSize: "45px",
                    color: "blue",
                    fontFamily: "Lato",
                  }}
                >
                  Announcement:{" "}
                </span>
                <input
                  style={{
                    width: "35px",
                    height: "35px",
                  }}
                  type="checkbox"
                  defaultChecked={device?.announcement ?? true}
                  onChange={(e) => {
                    updateDevice({ announcement: e.target.checked });
                  }}
                />
              </div>
              <div
                style={{
                  marginTop: 20,
                }}
              >
                <span
                  style={{
                    fontSize: "45px",
                    color: "blue",
                    fontFamily: "Lato",
                  }}
                >
                  Music:{" "}
                </span>
                <input
                  style={{
                    width: "35px",
                    height: "35px",
                  }}
                  type="checkbox"
                  defaultChecked={device?.trc_music ?? true}
                  onChange={(e) => {
                    updateDevice({ trc_music: e.target.checked });
                  }}
                />
              </div>
            </>
          )}
        </div>
      ) : view === "VacationDate" ? (
        <>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: isTv ? "flex-start" : "center",
              gap: isTv ? "40px" : "20px",
              paddingTop: isTv ? "50px" : "0px",
              height: "100%",
              width: "100%",
            }}
          >
            <h2
              style={{
                fontSize: isTv ? "100px" : "40px",
                color: "blue",
                fontFamily: "Lato",
                marginBottom: isTv ? "40px" : "20px",
                textAlign: "center",
              }}
            >
              Set Vacation Date
            </h2>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: isTv ? "30px" : "20px",
              }}
            >
              <label
                style={{
                  fontSize: isTv ? "50px" : "20px",
                  color: "blue",
                  fontFamily: "Lato",
                  textAlign: "center",
                }}
              >
                Select Vacation Date
              </label>
              <input
                ref={firstVacationButtonRef}
                type="date"
                value={vacationDate}
                onChange={(e) => {
                  setVacationDate(e.target.value);
                  // Clear error when user selects a date
                  if (vacationDateError) {
                    setVacationDateError("");
                  }
                }}
                onFocus={() => setFocusedButton("vacation-date-input")}
                onBlur={() => setFocusedButton(null)}
                autoFocus={isTv}
                style={{
                  width: isTv ? "600px" : "250px",
                  height: isTv ? "120px" : "50px",
                  fontSize: isTv ? "50px" : "20px",
                  textAlign: "center",
                  border:
                    isTv && focusedButton === "vacation-date-input"
                      ? "6px solid #3ADF3C"
                      : isTv
                      ? "6px solid blue"
                      : "2px solid blue",
                  borderRadius: "15px",
                  outline: "none",
                  padding: isTv ? "15px" : "10px",
                  fontFamily: "Lato",
                  fontWeight: "600",
                }}
              />
            </div>

            {vacationDateError && (
              <div
                style={{
                  color: "red",
                  fontSize: isTv ? "35px" : "18px",
                  fontFamily: "Lato",
                  fontWeight: "600",
                  textAlign: "center",
                  marginTop: isTv ? "20px" : "10px",
                }}
              >
                {vacationDateError}
              </div>
            )}

            <div
              style={{
                display: "flex",
                gap: isTv ? "40px" : "20px",
                marginTop: isTv ? "60px" : "40px",
                justifyContent: "center",
                width: "100%",
              }}
            >
              <button
                tabIndex={0}
                onFocus={() => setFocusedButton("vacation-submit")}
                onBlur={() => setFocusedButton(null)}
                onClick={handleVacationDateSubmit}
                style={getButtonStyle("vacation-submit", {
                  marginLeft: "10px",
                  border: isTv ? "6px solid green" : "2px solid green",
                  background: "transparent",
                  color: "green",
                  fontFamily: "Lato",
                  fontSize: isTv ? "50px" : "35px",
                  padding: isTv ? "10px 20px" : "10px 20px",
                  borderRadius: "25px",
                  fontWeight: "700",
                  margin: "10px 0px",
                  cursor: "pointer",
                  height: isTv ? "130px" : "80px",
                  minWidth: isTv ? "300px" : "auto",
                })}
              >
                Submit
              </button>

              <button
                tabIndex={0}
                onFocus={() => setFocusedButton("vacation-cancel")}
                onBlur={() => setFocusedButton(null)}
                onClick={() => {
                  setView("all");
                  // Reset form
                  setVacationDate("");
                  setVacationDateError("");
                }}
                style={getButtonStyle("vacation-cancel", {
                  marginLeft: "10px",
                  border: isTv ? "6px solid red" : "2px solid red",
                  background: "transparent",
                  color: "red",
                  fontFamily: "Lato",
                  fontSize: isTv ? "50px" : "35px",
                  padding: isTv ? "10px 20px" : "10px 20px",
                  borderRadius: "25px",
                  fontWeight: "700",
                  margin: "10px 0px",
                  cursor: "pointer",
                  height: isTv ? "130px" : "80px",
                  minWidth: isTv ? "250px" : "auto",
                })}
              >
                Cancel
              </button>
            </div>
          </div>
        </>
      ) : view === "LivePiano" ? (
        <LivePianoSettings
          isTv={isTv}
          focusedButton={focusedButton}
          setFocusedButton={setFocusedButton}
          onClose={() => setView("all")}
        />
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <div style={{ display: "flex", flexDirection: "row", gap: "20px" }}>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <button
                ref={wifiButtonRef}
                tabIndex={0}
                onFocus={() => setFocusedButton("wifi")}
                onBlur={() => setFocusedButton(null)}
                onClick={() => {
                  goToWifiSettingsPageOnExpoApp();
                  setShowSettings(false);
                }}
                style={getButtonStyle("wifi", baseButtonStyle)}
              >
                Wifi Settings
              </button>
            </div>

            <div style={{ display: "flex", flexDirection: "column" }}>
              <button
                tabIndex={0}
                onFocus={() => setFocusedButton("autoscroll")}
                onBlur={() => setFocusedButton(null)}
                onClick={() => {
                  setView("AutoScroll");
                }}
                style={getButtonStyle("autoscroll", baseButtonStyle)}
              >
                Enable Auto Scroll
              </button>
            </div>

            <div style={{ display: "flex", flexDirection: "column" }}>
              <button
                tabIndex={0}
                onFocus={() => setFocusedButton("timezone")}
                onBlur={() => setFocusedButton(null)}
                onClick={() => {
                  setView("TimeZone");
                }}
                style={getButtonStyle("timezone", baseButtonStyle)}
              >
                Time Zone
              </button>
            </div>

            <div style={{ display: "flex", flexDirection: "column" }}>
              <button
                tabIndex={0}
                onFocus={() => setFocusedButton("vacation-date")}
                onBlur={() => setFocusedButton(null)}
                onClick={() => {
                  setView("VacationDate");
                }}
                style={getButtonStyle("vacation-date", baseButtonStyle)}
              >
                Vacation Date
              </button>
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "row", gap: "20px" }}>
            {!isTv && (
              <div style={{ display: "flex", flexDirection: "column" }}>
                <button
                  tabIndex={0}
                  onFocus={() => setFocusedButton("volume")}
                  onBlur={() => setFocusedButton(null)}
                  onClick={() => {
                    setView("Volume");
                  }}
                  style={getButtonStyle("volume", baseButtonStyle)}
                >
                  Volume
                </button>
              </div>
            )}

            <div style={{ display: "flex", flexDirection: "column" }}>
              <button
                tabIndex={0}
                onFocus={() => setFocusedButton("terrace")}
                onBlur={() => setFocusedButton(null)}
                onClick={() => {
                  setView("TRC");
                }}
                style={getButtonStyle("terrace", baseButtonStyle)}
              >
                Terrace
              </button>
            </div>

            <div style={{ display: "flex", flexDirection: "column" }}>
              <button
                tabIndex={0}
                onFocus={() => setFocusedButton("live-piano")}
                onBlur={() => setFocusedButton(null)}
                onClick={() => {
                  setView("LivePiano");
                }}
                style={getButtonStyle("live-piano", baseButtonStyle)}
              >
                Live Piano
              </button>
            </div>

            <div style={{ display: "flex", flexDirection: "column" }}>
              <button
                tabIndex={0}
                onFocus={() => setFocusedButton("update")}
                onBlur={() => setFocusedButton(null)}
                onClick={handleUpdate}
                style={getButtonStyle("update", {
                  marginLeft: "10px",
                  border: isUpdateAvailable()
                    ? isTv
                      ? "6px solid blue"
                      : "2px solid blue"
                    : isTv
                    ? "6px solid grey"
                    : "2px solid grey",
                  background: "transparent",
                  color: isUpdateAvailable() ? "blue" : "grey",
                  fontFamily: "Lato",
                  fontSize: isTv ? "60px" : "35px",
                  padding: isTv ? "10px 25px" : "0px 20px",
                  borderRadius: "20px",
                  fontWeight: "700",
                  margin: "10px 0px",
                  cursor: "pointer",
                })}
                disabled={!isUpdateAvailable()}
              >
                Update
              </button>
            </div>

            <div style={{ display: "flex", flexDirection: "column" }}>
              <button
                tabIndex={0}
                onFocus={() => setFocusedButton("developer")}
                onBlur={() => setFocusedButton(null)}
                onClick={() => {
                  if (isTv) {
                    window.ReactNativeWebView.postMessage(
                      JSON.stringify({
                        action: "exit-app",
                      })
                    );
                  } else {
                    setShowPassInput(true);
                  }
                }}
                style={getButtonStyle("developer", baseButtonStyle)}
              >
                Developer
              </button>
            </div>
          </div>

          <br />
          {/*<span style={{fontWeight: 'bold', fontSize: '20px'}}>Time Zone Settings</span>*/}

          {isTv && (
            <div style={{ marginTop: "20px" }}>
              <button
                tabIndex={0}
                onFocus={() => setFocusedButton("exit")}
                onBlur={() => setFocusedButton(null)}
                onClick={() => {
                  setShowSettings(false);
                  document.getElementById(buttonIds.settings).focus();
                }}
                style={getButtonStyle("exit", exitButtonStyle)}
              >
                Exit
              </button>
            </div>
          )}
        </div>
      )}

      {showPassInput && (
        <div
          style={{
            backgroundColor: "blue",
            width: "100vw",
            height: "100vh",
            position: "absolute",
            top: "0%",
            left: "0%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
          }}
        >
          {/* X button to close the passcode modal */}
          <div
            style={{ position: "absolute", top: "5%", right: "3%" }}
            onClick={() => setShowPassInput(false)} // Close the modal on clicking X
          >
            <IoCloseSharp size={60} color="white" />
          </div>
          <span
            style={{
              fontSize: "30px",
              color: "white",
              fontFamily: "Lato",
            }}
          >
            Developer Support Portal: Password Required
          </span>
          <input
            onChange={(e) => setPasscode(e.target.value)}
            type="password"
            placeholder="Enter Passcode"
            style={{
              width: "300px",
              height: "50px",
              fontSize: "20px",
              padding: "20px",
              borderRadius: "10px",
              border: "1px solid grey",
              marginTop: "20px",
            }}
          />
          <button
            tabIndex={0}
            onFocus={() => setFocusedButton("submit-passcode")}
            onBlur={() => setFocusedButton(null)}
            style={getButtonStyle("submit-passcode", {
              width: "300px",
              padding: "20px",
              borderRadius: "10px",
              border: "none",
              marginTop: "20px",
              backgroundColor: "green",
            })}
            onClick={() => {
              if (passcode === "2441138") {
                console.log("exit app");
                window.ReactNativeWebView.postMessage(
                  JSON.stringify({
                    action: "exit-app",
                  })
                );
              }
            }}
          >
            <span
              style={{
                fontSize: "30px",
                color: "white",
                fontFamily: "Lato",
              }}
            >
              Submit
            </span>
          </button>
        </div>
      )}
    </div>
  );
};

export default Settings;
